// capabilities - what the reader of a report may do beyond reading it.
//
// The dashboard grants everything, and with no provider that is what every
// report page gets. An app that hosts the report pages for readers with
// fewer rights supplies its own value, and a control the reader may not use
// becomes a link to where they can get it, or disappears when there is
// nowhere to send them. A button that fails on click is the outcome this
// exists to prevent.

import { createContext, type ReactNode, useContext } from 'react';

export interface ReportCapabilities {
  /** The result archive, its files, dropped files, the PCAP and the raw
   *  report JSON. */
  downloadRaw: boolean;
  /** Fetching the submitted document's bytes to render its pages. */
  previewSample: boolean;
  /** Submitting a file out of a run as an analysis of its own. */
  detonate: boolean;
  /** Steering a live run: input to its screen, finishing it early,
   *  cancelling it and asking for a verdict before it ends. */
  drive: boolean;
  /** Where a reader goes to gain what they lack. Null hides a withheld
   *  control instead of offering a link. */
  upgradeHref: string | null;
}

const EVERYTHING: ReportCapabilities = {
  downloadRaw: true,
  previewSample: true,
  detonate: true,
  drive: true,
  upgradeHref: null,
};

const ReportCapabilitiesContext = createContext<ReportCapabilities>(EVERYTHING);

export function ReportCapabilitiesProvider({
  value,
  children,
}: {
  value: ReportCapabilities;
  children: ReactNode;
}) {
  return <ReportCapabilitiesContext.Provider value={value}>{children}</ReportCapabilitiesContext.Provider>;
}

export function useReportCapabilities(): ReportCapabilities {
  return useContext(ReportCapabilitiesContext);
}

/**
 * What a withheld control becomes: a link to upgradeHref, or nothing when
 * there is nowhere to send the reader. `label` is the link's text, or its
 * accessible name when `children` draws something else, such as an icon in a
 * dense row.
 */
export function UpgradeLink({
  label,
  className,
  children,
}: {
  label: string;
  className?: string;
  children?: ReactNode;
}) {
  const { upgradeHref } = useReportCapabilities();
  if (!upgradeHref) return null;
  return (
    <a href={upgradeHref} className={className} title={label}>
      {children ?? label}
    </a>
  );
}
